import * as yup from 'yup'
import {
    type SupportedLanguage,
    supportedLanguages,
    type SupportedLocale,
    supportedLocales,
} from '../../i18n/i18n.no-deps'
import type { NotificationReason, ProfileCircleColor } from 'zaptic-types'
import { notificationReasons } from '../../domain/notifications/reasons.no-deps'

/**
 * BE CAREFUL using yup. It automatically coerces values by default.
 * This happens to be helpful for attributes which are all strings.
 * However if used elsewhere, please consider using .strict(true)
 */

/**
 * When setting an attribute like <div my-attribute> this is meant to mean true but is ''
 * Yup will automatically true to coerce a string like 'true' to boolean
 */
export const booleanAttributeSchema = yup
    .boolean()
    .nullable()
    .transform((value) => {
        if (value === '') return true
        return value
    })

/**
 * Need to use min(0) to set positive integers because yup does not include 0 as positive
 * By default yup will automatically coerce a string into a number if possible
 */
export const positiveIntegerAttributeSchema = yup.number().nullable().min(0).integer()

export function oneOfStringsAttributeSchema(...values: string[]) {
    return (
        yup
            .string()
            .nullable()
            /**
             * Need to add null as one of the accepted values
             * https://github.com/jquense/yup/issues/104
             */
            .oneOf([...values, null])
    )
}

export const uuidAttributeSchema = yup.string().uuid()

export const dateAttributeSchema = yup.date().nullable()

export const langAttributeSchema = oneOfStringsAttributeSchema(...supportedLanguages) as yup.Schema<SupportedLanguage>
export const localeAttributeSchema = oneOfStringsAttributeSchema(...supportedLocales) as yup.Schema<SupportedLocale>

export const profileCircleColorAttributeSchema = oneOfStringsAttributeSchema(
    'purple',
    'blue',
    'green',
    'pink',
    'orange',
    'red'
) as yup.Schema<ProfileCircleColor>

export const notificationReasonAttributeSchema = oneOfStringsAttributeSchema(
    ...notificationReasons
) as yup.Schema<NotificationReason>
